import { forwardRef } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { POSOrderWithItems } from '../../types/admin';

interface KitchenTicketProps {
  order: POSOrderWithItems;
  shopName: string;
}

export const KitchenTicket = forwardRef<HTMLDivElement, KitchenTicketProps>(
  ({ order, shopName }, ref) => {
    return (
      <div ref={ref} className="bg-white p-6 max-w-[80mm] mx-auto font-mono">
        {/* Header - Large and Bold */}
        <div className="text-center border-b-4 border-black pb-3 mb-4">
          <h1 className="text-2xl font-bold mb-1">CUISINE</h1>
          <p className="text-sm">{shopName}</p>
        </div>

        {/* Order Info - Prominent */}
        <div className="mb-4 space-y-2 text-base">
          <div className="flex justify-between items-center bg-black text-white px-3 py-2">
            <span className="font-bold">Commande:</span>
            <span className="text-xl font-bold">{order.order_number}</span>
          </div>
          
          {order.table_id && (
            <div className="flex justify-between items-center bg-gray-800 text-white px-3 py-2">
              <span className="font-bold">Table:</span>
              <span className="text-2xl font-bold">{order.table_id}</span>
            </div>
          )}

          <div className="flex justify-between text-sm">
            <span>Heure:</span>
            <span className="font-bold">{format(new Date(order.created_at), 'HH:mm', { locale: fr })}</span>
          </div>

          {order.order_type === 'takeaway' && (
            <div className="bg-yellow-100 border-2 border-yellow-500 px-3 py-2 text-center font-bold">
              À EMPORTER
            </div>
          )}

          {order.order_type === 'delivery' && (
            <div className="bg-blue-100 border-2 border-blue-500 px-3 py-2 text-center font-bold">
              LIVRAISON
            </div>
          )}
        </div>

        {/* Items - Large and Clear */}
        <div className="border-t-4 border-black pt-3 mb-4">
          <div className="space-y-4">
            {order.items?.map((item, index) => (
              <div 
                key={item.id} 
                className="border-2 border-gray-300 rounded p-3 bg-gray-50"
              >
                {/* Quantity and Name */}
                <div className="flex items-start gap-3 mb-2">
                  <div className="w-12 h-12 bg-black text-white rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0">
                    {item.quantity}
                  </div>
                  <div className="flex-1">
                    <p className="text-lg font-bold leading-tight">
                      {item.product_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      Article {index + 1} / {order.items?.length || 0}
                    </p>
                  </div>
                </div>

                {/* Notes - Highlighted */}
                {item.notes && (
                  <div className="mt-2 bg-yellow-100 border-l-4 border-yellow-500 p-2">
                    <p className="text-xs font-bold text-gray-700 mb-1">⚠️ NOTES:</p>
                    <p className="text-sm font-bold text-gray-900">{item.notes}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Order Notes */}
        {order.notes && (
          <div className="border-4 border-red-500 bg-red-50 p-3 mb-4">
            <p className="text-xs font-bold text-red-700 mb-1">⚠️ NOTES COMMANDE:</p>
            <p className="text-base font-bold text-red-900">{order.notes}</p>
          </div>
        )}

        {/* Summary */}
        <div className="border-t-4 border-black pt-3 space-y-2 text-base">
          <div className="flex justify-between font-bold">
            <span>Total articles:</span>
            <span>{order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0}</span>
          </div>
          <div className="flex justify-between text-sm text-gray-600">
            <span>Heure commande:</span>
            <span>{format(new Date(order.created_at), 'HH:mm:ss', { locale: fr })}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 pt-4 border-t-2 border-dashed border-gray-400">
          <div className="inline-block px-4 py-2 bg-black text-white font-bold text-lg">
            {order.order_number}
          </div>
        </div>
      </div>
    );
  }
);

KitchenTicket.displayName = 'KitchenTicket';
